/**
 * Card Component
 * Premium glassmorphism card with gradient borders and hover effects
 */

import React from "react";
import { cn } from "../../utils";

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: "none" | "sm" | "md" | "lg" | "xl";
  variant?: "default" | "glass" | "gradient" | "bordered" | "elevated";
  hover?: boolean;
  glow?: boolean;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  padding = "md",
  variant = "default",
  hover = false,
  glow = false,
  onClick,
}) => {
  const paddings = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
    xl: "p-10",
  };

  const variants = {
    default: `
      bg-slate-800/50 backdrop-blur-sm
      border border-slate-700/50
    `,
    glass: `
      bg-gradient-to-br from-slate-800/60 to-slate-900/80
      backdrop-blur-xl
      border border-slate-700/30
    `,
    gradient: `
      bg-gradient-to-br from-slate-800/80 via-slate-800/60 to-slate-900/80
      backdrop-blur-xl
      border border-transparent
      relative overflow-hidden
      before:absolute before:inset-0 before:p-[1px] before:rounded-xl
      before:bg-gradient-to-br before:from-primary-500/20 before:to-accent-500/20
      before:-z-10
    `,
    bordered: `
      bg-slate-900/50 backdrop-blur-sm
      border-2 border-primary-500/20
    `,
    elevated: `
      bg-slate-800/70 backdrop-blur-sm
      border border-slate-700/50
      shadow-xl shadow-black/20
    `,
  };

  const hoverStyles = hover
    ? `
      transition-all duration-300 ease-out
      hover:bg-slate-800/70
      hover:border-primary-500/30
      hover:shadow-xl hover:shadow-primary-500/10
      hover:-translate-y-1
      cursor-pointer
    `
    : "transition-all duration-200";

  const glowStyles = glow ? "shadow-glow-sm hover:shadow-glow-md" : "";

  return (
    <div
      className={cn(
        "rounded-xl",
        variants[variant],
        hoverStyles,
        glowStyles,
        paddings[padding],
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

interface CardHeaderProps {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  icon,
  className,
}) => {
  return (
    <div className={cn("flex items-start justify-between mb-6", className)}>
      <div className="flex items-center gap-4">
        {icon && (
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 text-primary-400">
            {icon}
          </div>
        )}
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {subtitle && (
            <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
};

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  noBorder?: boolean;
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className,
  noBorder = false,
}) => {
  return (
    <div
      className={cn(
        "mt-6 pt-6",
        !noBorder && "border-t border-slate-700/50",
        className
      )}
    >
      {children}
    </div>
  );
};

// New: Stats Card Component
interface StatsCardProps {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  value,
  label,
  icon,
  trend,
  className,
}) => {
  return (
    <Card variant="glass" padding="lg" hover className={className}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-400 mb-2">{label}</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
            {value}
          </p>
          {trend && (
            <p
              className={cn(
                "text-sm mt-2 font-medium",
                trend.isPositive ? "text-emerald-400" : "text-red-400"
              )}
            >
              {trend.isPositive ? "↑" : "↓"} {Math.abs(trend.value)}%
            </p>
          )}
        </div>
        {icon && (
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 text-primary-400">
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
};

// New: Feature Card Component
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  badgeVariant?: "primary" | "accent" | "success" | "warning";
  className?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  badge,
  badgeVariant = "primary",
  className,
}) => {
  const badgeColors = {
    primary: "bg-primary-500/15 text-primary-400 border-primary-500/30",
    accent: "bg-accent-500/15 text-accent-400 border-accent-500/30",
    success: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    warning: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  };

  return (
    <Card variant="glass" padding="lg" hover className={className}>
      <div className="flex items-start gap-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 text-primary-400 flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
          <p className="text-sm text-slate-400 leading-relaxed mb-3">
            {description}
          </p>
          {badge && (
            <span
              className={cn(
                "inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg border",
                badgeColors[badgeVariant]
              )}
            >
              {badge}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};
